/* JUST START Product Discovery Engine — client runtime.
 *
 * Scope, deliberately small:
 *   1. Remember which products this visitor opened, on this device only.
 *   2. Render safe navigation history back to them, with zero network requests.
 *
 * What it is not: this is personal browsing history, not a popularity signal.
 * Nothing here is shared, aggregated or sent anywhere, so nothing here may be
 * labelled "most viewed" or "trending" — the theme has no access to store-wide
 * behaviour and does not pretend otherwise.
 *
 * Price, availability and buy controls are intentionally absent. They can turn
 * stale after the visit; zero network requests is only honest if the resulting
 * tile is navigational rather than transactional.
 */
(() => {
  'use strict';

  /* The layout loads this on product and collection pages; a recently-viewed
     section loads it again wherever it is placed. Running twice would double
     the observers and record the visit twice. */
  if (window.JUSTSTART && window.JUSTSTART.recentlyViewed) return;

  const locale = String(document.documentElement.lang || 'default').toLowerCase();
  const KEY = `jst_recently_viewed_v2_${locale}`;
  const MAX = 12;
  const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

  /* ---------------------------------------------------------------- storage */

  /* Private browsing, disabled storage and quota errors all surface as throws.
     Every path below degrades to "no history", never to a broken page. */
  const readStore = () => {
    try {
      const raw = window.localStorage.getItem(KEY);
      const list = raw ? JSON.parse(raw) : [];
      if (!Array.isArray(list)) return [];
      const now = Date.now();
      return list.filter((item) => item && item.handle && now - (item.ts || 0) < MAX_AGE_MS);
    } catch (error) {
      return [];
    }
  };

  const writeStore = (list) => {
    try {
      window.localStorage.setItem(KEY, JSON.stringify(list.slice(0, MAX)));
    } catch (error) {
      /* Storage full or unavailable: the visit simply is not remembered. */
    }
  };

  /* ---------------------------------------------------------------- tracking */

  const track = (entry) => {
    if (!entry || !entry.handle) return;
    const list = readStore().filter((item) => item.handle !== entry.handle);
    list.unshift({ ...entry, ts: Date.now() });
    writeStore(list);
  };

  const trackCurrentPage = () => {
    const node = document.querySelector('[data-jst-track-view]');
    if (!node) return;
    try {
      track(JSON.parse(node.getAttribute('data-jst-track-view')));
    } catch (error) {
      /* A malformed payload must not stop the page. */
    }
  };

  /* ---------------------------------------------------------------- render */

  const escapeHtml = (value) => String(value == null ? '' : value)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');

  /* Shopify CDN URLs carry their render width as a query parameter, so a
     ladder can be rebuilt from one stored URL without a second request. The
     widths and sizes are not hardcoded here — they are read from the
     collection-card profile that Liquid printed into the section, so the
     Visual Engine stays the single source of truth for both. */
  const resizeUrl = (url, width) => {
    if (!url) return '';
    if (/([?&])width=\d+/.test(url)) return url.replace(/([?&])width=\d+/, `$1width=${width}`);
    return `${url}${url.includes('?') ? '&' : '?'}width=${width}`;
  };

  const safeProductUrl = (value) => {
    try {
      const url = new URL(value, window.location.origin);
      if (url.origin !== window.location.origin) return '#';
      return `${url.pathname}${url.search}${url.hash}`;
    } catch (error) {
      return '#';
    }
  };

  const safeImageUrl = (value) => {
    try {
      const url = new URL(value, window.location.href);
      return /^(https?:)$/.test(url.protocol) ? url.href : '';
    } catch (error) {
      return '';
    }
  };

  const buildSrcset = (url, widths, sourceWidth, cap) => {
    if (!url || !widths || !widths.length) return '';
    const source = Math.max(0, Number(sourceWidth) || 0);
    const ceiling = Math.max(1, Math.min(Number(cap) || Infinity, source || Infinity));
    const candidates = [...new Set(widths.map(Number).filter((width) => width > 0 && width <= ceiling))];
    if (source > 0 && source <= (Number(cap) || source) && !candidates.includes(source)) candidates.push(source);
    return candidates.sort((a, b) => a - b)
      .map((width) => `${resizeUrl(url, width)} ${width}w`)
      .join(', ');
  };

  const cardMarkup = (item, policy) => {
    const href = escapeHtml(safeProductUrl(item.url));
    const title = escapeHtml(item.title);
    const image = safeImageUrl(item.image);
    const sourceWidth = Math.max(1, Number(item.imageWidth) || 600);
    const sourceHeight = Math.max(1, Number(item.imageHeight) || 750);
    const media = image
      ? `<img class="jst-history-card__image" src="${escapeHtml(image)}"
             srcset="${escapeHtml(buildSrcset(image, policy.widths, sourceWidth, policy.cap))}"
             sizes="${escapeHtml(policy.sizes)}"
             alt="${title}" width="${sourceWidth}" height="${sourceHeight}" loading="lazy" decoding="async" fetchpriority="low">`
      : '<span class="jst-history-card__placeholder" aria-hidden="true"></span>';

    return `<div class="jst-rail__item">
      <article class="jst-history-card">
        <a href="${href}" class="jst-history-card__media">${media}</a>
        <h3 class="jst-history-card__title"><a href="${href}">${title}</a></h3>
      </article>
    </div>`;
  };

  const hydrate = (section) => {
    if (section.dataset.hydrated === 'true') return;

    const target = section.querySelector('[data-jst-recently-viewed-target]');
    if (!target) return;

    let policy = { widths: [400, 600, 900], sizes: '(max-width: 700px) 45vw, 300px' };
    try {
      const parsed = JSON.parse(section.getAttribute('data-media-policy'));
      if (parsed && Array.isArray(parsed.widths) && parsed.widths.length) policy = parsed;
    } catch (error) {
      /* Fall back to the conservative default above. */
    }

    const limit = Math.max(1, Math.min(MAX, parseInt(section.dataset.limit, 10) || 8));
    const current = document.querySelector('[data-jst-track-view]');
    let currentHandle = null;
    try {
      currentHandle = current ? JSON.parse(current.getAttribute('data-jst-track-view')).handle : null;
    } catch (error) {
      currentHandle = null;
    }

    const items = readStore().filter((item) => item.handle !== currentHandle).slice(0, limit);
    /* Nothing to show is not an error state and not an empty titled block:
       the shell simply never becomes visible. */
    if (!items.length) return;

    section.dataset.hydrated = 'true';
    const layout = section.dataset.layout === 'snap' ? 'snap' : 'grid';
    target.innerHTML =
      `<div class="jst-rail jst-rail--${layout}" data-cols="${escapeHtml(section.dataset.cols || '4')}" data-jst-rail>` +
      items.map((item) => cardMarkup(item, policy)).join('') +
      '</div>';
    section.removeAttribute('hidden');
  };

  /* Hydration is scheduled on idle, not on scroll.
     An IntersectionObserver would be the obvious choice and would be wrong
     here: the section ships with `hidden`, a hidden element has no box, and an
     element with no box never intersects anything — it would wait forever.
     Deferring on visibility buys nothing anyway, because this render makes no
     network requests at all; idle time is the only thing worth yielding. */
  const schedule = window.requestIdleCallback
    ? (task) => window.requestIdleCallback(task, { timeout: 2000 })
    : (task) => window.setTimeout(task, 200);

  const watch = (scope) => {
    const sections = (scope || document).querySelectorAll('[data-jst-recently-viewed]:not([data-hydrated])');
    if (!sections.length) return;
    schedule(() => sections.forEach(hydrate));
  };

  /* ------------------------------------------------------- collection controls

     Progressive enhancement only. The form already works as a form; this makes
     it submit on change and hides the now-redundant button. The button is
     hidden from here rather than from CSS precisely so that a visitor without
     scripting keeps a working control instead of an inert one. */
  const enhanceControls = (scope) => {
    (scope || document).querySelectorAll('[data-jst-collection-controls]:not([data-auto-submit])').forEach((form) => {
      form.dataset.autoSubmit = 'true';
      form.addEventListener('change', () => form.requestSubmit());
    });
  };

  /* ---------------------------------------------------------------- wiring */

  trackCurrentPage();
  watch(document);
  enhanceControls(document);

  /* The Theme Editor re-renders a section in place; its new shell has to be
     picked up without a reload. */
  document.addEventListener('shopify:section:load', (event) => {
    watch(event.target);
    enhanceControls(event.target);
  });

  window.JUSTSTART = window.JUSTSTART || {};
  window.JUSTSTART.recentlyViewed = { read: readStore, track: track };
})();
